import { useState, useEffect } from 'react';

import { mediaQueriesMaxPxConfig } from '@/config/mediaQueries';

export enum Breakpoints {
  SM = 'sm',
  MD = 'md',
  LG = 'lg',
  XL = 'xl',
  XXL = 'xxl',
}

interface BreakpointRange {
  min?: number;
  max?: number;
}

const { screenSM, screenMD, screenLG, screenXL, screenXXL } = mediaQueriesMaxPxConfig;

const breakpoints: Record<Breakpoints, BreakpointRange> = {
  sm: { max: screenSM },
  md: { max: screenMD },
  lg: { max: screenLG },
  xl: { max: screenXL },
  xxl: { max: screenXXL },
};

function getMediaQuery(breakpoint: Breakpoints): string {
  const { min, max } = breakpoints[breakpoint];

  if (max !== undefined) {
    return min !== undefined ? `(min-width: ${min}px) and (max-width: ${max}px)` : `(max-width: ${max}px)`;
  }
  if (min !== undefined) {
    return `(min-width: ${min}px)`;
  }
  return '';
}

export const useMediaQuery = (breakpoint: Breakpoints): boolean => {
  const query = getMediaQuery(breakpoint);
  const [matches, setMatches] = useState<boolean>(() =>
    typeof window !== 'undefined' ? window.matchMedia(query).matches : false,
  );

  useEffect(() => {
    const mediaQueryList = window.matchMedia(query);
    const listener = (event: MediaQueryListEvent): void => {
      setMatches(event.matches);
    };

    if (typeof mediaQueryList.addEventListener === 'function') {
      mediaQueryList.addEventListener('change', listener);

      return () => {
        mediaQueryList.removeEventListener('change', listener);
      };
    }
  }, [query]);

  return matches;
};
